import React, { useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  ShieldCheck,
  BookOpen,
  CreditCard,
  GraduationCap,
  ArrowUpCircle,
  UserCheck,
  ChevronRight,
  PiggyBank,
  FileCheck,
  ClipboardList,
  Printer,
  Info,
  CalendarCheck,
} from "lucide-react";
import { useGetMyFinanceSummaryQuery } from "../../../modules/billing/api/billingApi";
import { useGetMyExamApplicationsQuery } from "../../exam/api/examsApi";
import { useGetMyCpdLedgerQuery } from "../../cpd/api/cpdApi";

import { formatCurrency } from "../../../utils/formatters";
import { useMeQuery } from "../../../modules/auth/api/authApi";
import { useAuth } from "../../../context/AuthContexts";

const APP_STATUS_COLORS: Record<string, string> = {
  draft:     "bg-gray-100 text-gray-500",
  submitted: "bg-blue-100 text-blue-700",
  paid:      "bg-cyan-100 text-cyan-700",
  approved:  "bg-emerald-100 text-emerald-700",
  rejected:  "bg-rose-100 text-rose-700",
  completed: "bg-indigo-100 text-indigo-700",
};

/* ----------------------------------------
   Helpers
---------------------------------------- */
const norm = (v: any) =>
  String(v ?? "")
    .toLowerCase()
    .trim();

function normalizeRole(input?: string) {
  const r = norm(input);
  if (["admin", "administrator", "superadmin", "super_admin"].includes(r)) return "admin";
  if (["past president", "past_president", "past-president"].includes(r)) return "past president";
  if (["president"].includes(r)) return "president";
  if (["fellow"].includes(r)) return "fellow";
  if (["member", "corporate"].includes(r)) return "member";
  if (["probationer"].includes(r)) return "probationer";
  if (["graduate"].includes(r)) return "graduate";
  if (["technician"].includes(r)) return "technician";
  return "member";
}

// Roles arrive either as plain strings or as {name, permissions} objects.
function roleName(r: any): string {
  return typeof r === "string" ? r : (r?.name ?? "");
}

function prettyRole(role?: string | null) {
  if (!role) return "Member";
  return role
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}
/* ----------------------------------------
   Dashboard Component
---------------------------------------- */
export default function DashboardHome() {
  const { data: userData } = useMeQuery();
  const STORAGE_KEY = "logbook_auth_user_v2";
  const raw = localStorage.getItem(STORAGE_KEY);
  const user = userData?.user || (raw ? JSON.parse(raw) : null);

  const navigate = useNavigate();
  const { data, isLoading: isFinanceLoading } = useGetMyFinanceSummaryQuery();
  const { data: examApps = [], isLoading: examLoading } = useGetMyExamApplicationsQuery();
  const { data: cpdData } = useGetMyCpdLedgerQuery();
  const totalCpd = cpdData?.total ?? 0;

  const totalExpected = data?.grand_summary?.total_owed ?? 0;
  const advance = data?.grand_summary?.advance ?? 0;
  const isAuthLoading = !user;
  const pageIsLoading = isAuthLoading || isFinanceLoading;

  // Whatever the user picked in the topbar role switcher wins — the dashboard
  // should mirror the privilege they are currently acting under, not just the
  // first role on their account.
  const { activeRoleName } = useAuth();
  const rawRole = useMemo(
    () =>
      activeRoleName ||
      roleName(user?.roles?.[0]) ||
      (user as any)?.whois ||
      "",
    [activeRoleName, user],
  );
  const userRole = useMemo(() => normalizeRole(rawRole), [rawRole]);
  const roleLabel = useMemo(() => prettyRole(rawRole), [rawRole]);
  const yearsAsMember = (user as any)?.years_as_member || 0;
  const isDebtFree = (user as any)?.is_financially_up_to_date || false;
  // A user with no members-table profile is not yet a member — member-only
  // dashboard features (logbook, CPD, exams, applications, upgrade) are hidden.
  const hasMember = !!(user as any)?.member_id;

  const kpis = useMemo(() => {
    if (userRole === "admin") {
      return [
        {
          title: "Total Users",
          value: "0",
          icon: <UserCheck size={20} />,
          color: "text-blue-600",
          bg: "bg-blue-50",
          path: "/admin/users",
        },
        {
          title: "Pending Approvals",
          value: "0",
          icon: <ShieldCheck size={20} />,
          color: "text-yellow-600",
          bg: "bg-yellow-50",
          path: "/admin/approvals",
        },
        {
          title: "Reports",
          value: "—",
          icon: <BookOpen size={20} />,
          color: "text-purple-600",
          bg: "bg-purple-50",
          path: "/admin/reports",
        },
      ];
    }

    return [
      {
        title: "Outstanding Balance",
        value: `${formatCurrency(totalExpected)}`,
        icon: <CreditCard size={20} />,
        color: "text-red-600",
        bg: "bg-red-50",
        path: "/finance/summary",
      },
      {
        title: "Receipts",
        value: "0",
        icon: <FileCheck size={20} />,
        color: "text-green-600",
        bg: "bg-green-50",
        path: "/payment/my",
      },
      {
        title: "Advance",
        value: `${formatCurrency(advance)}`,
        icon: <PiggyBank size={20} />,
        color: "text-green-600",
        bg: "bg-green-50",
        path: "#",
      },
      ...( ["probationer", "graduate", "technician"].includes(userRole) && hasMember
        ? [
            {
              title: "Logbook",
              value: "0",
              icon: <BookOpen size={20} />,
              color: "text-blue-600",
              bg: "bg-blue-50",
              path: "/logbook",
            },
          ]
        : []),
      ...(hasMember
        ? [
            {
              title: "CPD Points",
              value: totalCpd % 1 === 0 ? totalCpd : totalCpd.toFixed(1),
              icon: <GraduationCap size={20} />,
              color: "text-teal-600",
              bg: "bg-teal-50",
              path: "/my-cpd",
            },
          ]
        : []),
    ];
  }, [userRole, totalExpected, totalCpd, hasMember]);

  const modules = useMemo(() => {
    // ✅ ADMIN MODULES
    if (userRole === "admin") {
      return [
        {
          title: "Admin Management",
          desc: "Manage all admins",
          icon: <UserCheck />,
          path: "/admins",
        },
        {
          title: "Member Analysis",
          desc: "See Membership Statistics",
          icon: <ShieldCheck />,
          path: "/member-list",
        },
        {
          title: "Payments",
          desc: "Monitor all transactions",
          icon: <CreditCard />,
          path: "/finance/analysis",
        },
        {
          title: "Reports",
          desc: "System analytics & exports",
          icon: <BookOpen />,
          path: "/admin/reports",
        },
      ];
    }

    // 👇 Existing logic
    const list: any[] = [
      {
        title: "Dues & Levies",
        desc: "View and settle outstanding payments",
        icon: <CreditCard />,
        path: "/my-invoices",
      },
      {
        title: "Payment Receipts",
        desc: "History of all successful transactions",
        icon: <FileCheck />,
        path: "/payment/my",
      },
    ];

    if (["probationer", "graduate", "technician"].includes(userRole) && hasMember) {
      list.push({
        title: "My Logbook",
        desc: "Manage entries and submissions",
        icon: <BookOpen />,
        path: "/logbook",
      });
    }

    if (

       (userRole === "fellow" && yearsAsMember >= 5 && isDebtFree) ||
      userRole === "past president" ||
      userRole === "president" ||
      (userRole === "member" && yearsAsMember >= 5 && isDebtFree)
    ) {
      list.push({
        title: "Supervise Logbook",
        desc: "Review trainee submissions",
        icon: <UserCheck />,
        path: "/supervisor",
      });
    }

    if (hasMember) {
      list.push({
        title: "My CPD",
        desc: "View your continuing professional development points",
        icon: <GraduationCap />,
        path: "/my-cpd",
      });
    }

    if (
      ["probationer", "graduate", "technician"].includes(userRole) &&
      isDebtFree &&
      hasMember
    ) {
      list.push({
        title: "Upgrade to Member",
        desc: "Begin your professional upgrade process",
        icon: <ArrowUpCircle />,
        path: "/upgrade-applications",
        featured: true,
      });
    } else if (userRole === "member" && yearsAsMember >= 10 && isDebtFree) {
      list.push({
        title: "Apply for Fellowship",
        desc: "For members with 10+ years corporate standing",
        icon: <ArrowUpCircle />,
        path: "/upgrade-applications",
        featured: true,
      });
    }

    return list;
  }, [userRole, yearsAsMember, isDebtFree, hasMember]);

  if (pageIsLoading) {
    return (
      <div className="fixed inset-0 bg-gray-50 flex flex-col items-center justify-center z-50">
        <div className="relative">
          {/* Outer Ring */}
          <div className="w-16 h-16 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin"></div>
          {/* Inner Logo/Icon Placeholder */}
          <div className="absolute inset-0 flex items-center justify-center">
            <LayoutDashboard
              size={20}
              className="text-blue-600 animate-pulse"
            />
          </div>
        </div>
        <p className="mt-4 text-sm font-black uppercase tracking-[0.2em] text-gray-400 animate-pulse">
          Synchronizing Portal...
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 p-4 md:p-6 relative">
      {/* HEADER SECTION */}
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-3">
            <LayoutDashboard className="text-blue-600" size={28} />
            Dashboard
          </h1>
          <p className="text-gray-500 mt-1 font-medium">
            Welcome back,{" "}
            <span className="text-blue-600">{user?.name || "Member"}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 bg-white border shadow-sm px-4 py-2 rounded-2xl">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-xs font-bold text-gray-700 uppercase tracking-widest">
            {roleLabel} Mode
          </span>
        </div>
      </header>

      {/* CPD MIGRATION NOTICE — non-admin members only */}
      {userRole !== "admin" && hasMember && (
        <div className="flex flex-col sm:flex-row sm:items-start gap-4 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4">
          <Info className="text-amber-500 shrink-0 mt-0.5" size={18} />
          <div className="flex-1">
            <p className="text-sm text-amber-800 font-medium leading-snug">
              <span className="font-black">CPD records are still being migrated.</span>{" "}
              Your total may not yet reflect all past events. If after <span className="font-black">72 hours</span> you believe any CPD points are missing,
              you can lodge a claim and it will be reviewed by an administrator.
            </p>
          </div>
          <Link
            to="/my-cpd?tab=submissions"
            className="shrink-0 self-start sm:self-center bg-amber-600 hover:bg-amber-700 text-white text-xs font-black uppercase tracking-widest px-4 py-2 rounded-xl transition-colors whitespace-nowrap"
          >
            Lodge a Claim
          </Link>
        </div>
      )}

      {/* KPI GRID */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpis.map((k) => (
          <button
            key={k.title}
            onClick={() => navigate(k.path)}
            className="flex items-center justify-between p-6 bg-white border border-gray-100 rounded-3xl shadow-sm hover:shadow-md hover:border-blue-100 transition-all text-left group"
          >
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                {k.title}
              </p>
              <h3 className="text-2xl font-black text-gray-800 mt-1 group-hover:text-blue-600 transition-colors">
                {k.value}
              </h3>
            </div>
            <div
              className={`p-4 rounded-2xl ${k.bg} ${k.color} group-hover:scale-110 transition-transform`}
            >
              {k.icon}
            </div>
          </button>
        ))}
      </section>

      {/* START AN APPLICATION — apply for an upgrade, exam, or event */}
      {userRole !== "admin" && hasMember && (
        <section className="space-y-3">
          <div className="flex items-center gap-2 px-2">
            <FileCheck className="text-indigo-500" size={20} />
            <h2 className="text-lg font-bold text-gray-800 uppercase tracking-tight">
              Start an Application
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                title: "Grade Upgrade",
                desc: "Apply to move up a membership grade",
                to: "/upgrade-applications",
                icon: <ArrowUpCircle size={22} />,
                tint: "bg-indigo-50 text-indigo-600",
                accent: "text-indigo-600",
                border: "hover:border-indigo-200",
              },
              {
                title: "Examinations",
                desc: "Browse open exam calls and apply",
                to: "/exams/calls",
                icon: <GraduationCap size={22} />,
                tint: "bg-orange-50 text-orange-600",
                accent: "text-orange-600",
                border: "hover:border-orange-200",
                hideFor: ["student"], // exams aren't open to students
              },
              {
                title: "Events",
                desc: "Discover and register for events",
                to: "/events",
                icon: <CalendarCheck size={22} />,
                tint: "bg-emerald-50 text-emerald-600",
                accent: "text-emerald-600",
                border: "hover:border-emerald-200",
              },
            ]
              .filter((c) => !(c as any).hideFor?.includes(userRole))
              .map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className={`group bg-white border border-gray-100 rounded-3xl p-5 shadow-sm hover:shadow-md ${c.border} transition-all flex flex-col gap-3`}
              >
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${c.tint}`}>
                  {c.icon}
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">{c.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{c.desc}</p>
                </div>
                <span className={`mt-auto inline-flex items-center gap-1 text-xs font-black ${c.accent} uppercase tracking-widest group-hover:gap-2 transition-all`}>
                  Apply <ChevronRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* EXAM APPLICATIONS — non-admin, member, and not students (exams aren't open to them) */}
      {userRole !== "admin" && String(userRole) !== "student" && hasMember && (
        <section className="space-y-3">
          <div className="flex items-center justify-between px-2">
            <div className="flex items-center gap-2">
              <ClipboardList className="text-orange-500" size={20} />
              <h2 className="text-lg font-bold text-gray-800 uppercase tracking-tight">
                My Exam Applications
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="h-px w-16 bg-gray-100" />
              <Link
                to="/exams/calls/applications"
                className="text-xs font-black text-orange-600 hover:text-orange-700 uppercase tracking-widest"
              >
                View All →
              </Link>
            </div>
          </div>

          {examLoading && (
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <div key={i} className="h-20 bg-gray-100 rounded-2xl animate-pulse" />
              ))}
            </div>
          )}

          {!examLoading && examApps.length === 0 && (
            <div className="bg-white border border-gray-100 rounded-3xl p-6 flex items-center justify-between shadow-sm">
              <div>
                <p className="text-sm font-bold text-gray-700">No exam applications yet</p>
                <p className="text-xs text-gray-400 mt-0.5">Browse open exams and apply below</p>
              </div>
              <Link
                to="/exams/calls"
                className="px-4 py-2 rounded-xl bg-gray-900 text-white text-xs font-black uppercase tracking-widest hover:bg-orange-600 transition-all"
              >
                Apply Now
              </Link>
            </div>
          )}

          {!examLoading && examApps.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {examApps.slice(0, 4).map((app: any) => {
                const excludedCodes = ["MRI", "PCI"];
                const canDownloadCard =
                  !["draft", "rejected"].includes(app.status) &&
                  !excludedCodes.includes((app.exam?.code ?? "").toUpperCase());
                return (
                  <div
                    key={app.id}
                    className="bg-white border border-gray-100 rounded-3xl p-5 shadow-sm hover:shadow-md transition-all"
                  >
                    {/* Top row: exam info + status */}
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 text-sm leading-tight truncate">
                          {app.exam?.title || "Exam"}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5 font-bold uppercase tracking-widest">
                          {app.exam?.code}
                        </p>
                      </div>
                      <span className={`flex-shrink-0 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full ${APP_STATUS_COLORS[app.status] ?? "bg-gray-100 text-gray-500"}`}>
                        {app.status}
                      </span>
                    </div>

                    {/* Diet + Centre */}
                    <p className="text-xs text-gray-500 mb-4">
                      <span className="font-semibold">Diet:</span> {app.diet?.title || "—"}
                      &nbsp;·&nbsp;
                      <span className="font-semibold">Centre:</span> {app.center?.name || "—"}
                    </p>

                    {/* Action buttons */}
                    {canDownloadCard && (
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/exams/applications/${app.id}/card`}
                          className="flex items-center justify-center gap-1.5 flex-1 py-2 rounded-xl bg-slate-900 text-white text-xs font-black uppercase tracking-widest hover:bg-orange-600 transition-all"
                        >
                          <Printer size={12} />
                          Admission Card
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {examApps.length > 4 && (
            <div className="text-center pt-1">
              <Link
                to="/exams/calls/applications"
                className="text-xs font-black text-gray-400 hover:text-orange-600 uppercase tracking-widest transition-colors"
              >
                + {examApps.length - 4} more application{examApps.length - 4 !== 1 ? "s" : ""} →
              </Link>
            </div>
          )}
        </section>
      )}

      {/* MODULES GRID */}
      <section className="space-y-4">
        <div className="flex items-center justify-between px-2">
          <h2 className="text-lg font-bold text-gray-800 uppercase tracking-tight">
            Main Features
          </h2>
          <span className="h-px flex-1 bg-gray-100 mx-4" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.map((m) => (
            <button
              key={m.title}
              onClick={m.action ? m.action : () => navigate(m.path)}
              className={`group relative flex flex-col p-6 rounded-3xl border text-left transition-all duration-300 ${
                m.featured
                  ? "bg-blue-600 border-blue-600 shadow-lg shadow-blue-100 hover:bg-blue-700"
                  : "bg-white border-gray-100 hover:border-blue-200 hover:shadow-xl hover:shadow-gray-100"
              }`}
            >
              <div className="flex items-start justify-between mb-8">
                <div
                  className={`p-3 rounded-2xl ${
                    m.featured
                      ? "bg-white/20 text-white"
                      : "bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white"
                  }`}
                >
                  {React.cloneElement(m.icon as React.ReactElement, {
                    size: 24,
                  })}
                </div>
                <ChevronRight
                  className={
                    m.featured
                      ? "text-white/50"
                      : "text-gray-300 group-hover:text-blue-500"
                  }
                  size={20}
                />
              </div>
              <h4
                className={`text-lg font-bold ${
                  m.featured ? "text-white" : "text-gray-800"
                }`}
              >
                {m.title}
              </h4>
              <p
                className={`text-sm mt-2 leading-relaxed ${
                  m.featured ? "text-blue-100" : "text-gray-500"
                }`}
              >
                {m.desc}
              </p>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
